import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import { ToolGlyph, ToolStatus } from '../components/tool-store/ToolStoreUI';
import { getSupernovaTool } from '../services/supernovaToolsService';
import '../styles/pages/ToolWorkspacePage.css';

const BUILDER_COPY = {
  experiment: {
    heading: 'Define the change.',
    intro:
      'Describe the challenge in plain language. You can refine it before creating the plan.',
    challengeLabel: 'Challenge',
    challengePlaceholder:
      'For example: residents abandon the permit application because the process is unclear…',
    affectedLabel: 'Who is affected?',
    affectedPlaceholder: 'Residents, teams or organisations',
    constraintLabel: 'What constraint matters most?',
    constraintPlaceholder: 'Time, policy, trust, access…',
    submitLabel: 'Build experiment plan',
    resultHeading: 'Your experiment plan',
    resultItems: [
      { key: 'challenge', label: 'Challenge', icon: 'target' },
      { key: 'hypothesis', label: 'Hypothesis', icon: 'flask' },
      { key: 'signal', label: 'Success signal', icon: 'chart' },
      { key: 'firstTest', label: 'First test', icon: 'sparkles' },
    ],
  },
  impact: {
    heading: 'Map the change.',
    intro:
      'Describe the intended change, who may experience it and the constraint shaping the work.',
    challengeLabel: 'Intended change',
    challengePlaceholder:
      'For example: make preventative health services easier to access…',
    affectedLabel: 'Who may experience the change?',
    affectedPlaceholder: 'Communities, teams or organisations',
    constraintLabel: 'What context matters most?',
    constraintPlaceholder: 'Access, cost, capability, trust…',
    submitLabel: 'Map intended impact',
    resultHeading: 'Your impact starting point',
    resultItems: [
      { key: 'challenge', label: 'Intended change', icon: 'target' },
      { key: 'hypothesis', label: 'Value hypothesis', icon: 'flask' },
      { key: 'signal', label: 'Early signal', icon: 'chart' },
      { key: 'firstTest', label: 'First mapping step', icon: 'sparkles' },
    ],
  },
};

const WORKSPACE_STEPS = {
  builder: ['Challenge', 'Context', 'Experiment plan'],
  chat: ['Question', 'Explore', 'Next steps'],
};

const buildPrototypePlan = (fields, kind) => {
  const affected = fields.affected.trim();
  const constraint = fields.constraint.trim();
  const challenge = fields.challenge.trim();

  if (kind === 'impact') {
    return {
      challenge,
      hypothesis: `If the proposed change is designed with ${affected} and responds to ${constraint.toLowerCase()}, it may create value across more than one form of impact.`,
      signal: `Look for an early, observable change in how ${affected} experience access, confidence or capability.`,
      firstTest: `Map one current-state journey with ${affected}, then record where ${constraint.toLowerCase()} affects the intended outcome.`,
    };
  }

  return {
    challenge,
    hypothesis: `If a small change addresses ${constraint.toLowerCase()} for ${affected}, then the experience should become clearer and easier to complete.`,
    signal: `Look for an observable improvement in completion, confidence or time-to-outcome for ${affected}.`,
    firstTest: `Run a small, time-boxed test with a limited group of ${affected}, capture what changes and record what still creates friction.`,
  };
};

const ToolWorkspaceHeader = ({ title, onReset, resetDisabled }) => (
  <header className="stw-header">
    <a className="stw-skip-link" href="#tool-workspace-main">
      Skip to workspace
    </a>
    <div className="stw-header__left">
      <Link className="stw-header__back" to="/supernova-tools">
        <ToolGlyph name="back" size={20} />
        All tools
      </Link>
    </div>

    <div className="stw-header__title">
      <strong>{title || 'Supernova Tools'}</strong>
    </div>

    <button disabled={resetDisabled} onClick={onReset} type="button">
      <ToolGlyph name="reset" size={19} />
      Reset
    </button>
  </header>
);

const ToolContextRail = ({ activeStep, tool }) => {
  const steps = WORKSPACE_STEPS[tool.experience] || WORKSPACE_STEPS.builder;

  return (
    <aside className="stw-context">
      <div>
        <h2>About this tool</h2>
        <p>{tool.longDescription}</p>
      </div>

      <dl className="stw-context__meta">
        <div>
          <dt>Type</dt>
          <dd>
            <ToolGlyph name={tool.icon} size={20} />
            {tool.type}
          </dd>
        </div>
        <div>
          <dt>Availability</dt>
          <dd>
            <ToolStatus tool={tool} />
          </dd>
        </div>
      </dl>

      <div className="stw-trust-note">
        <ToolGlyph name="lock" size={23} />
        <p>
          Your entries stay in this session and are cleared when you reset the
          tool.
        </p>
      </div>

      <ol className="stw-steps" aria-label="Tool progress">
        {steps.map((step, index) => {
          const stepNumber = index + 1;
          const state =
            stepNumber === activeStep
              ? 'is-active'
              : stepNumber < activeStep
                ? 'is-complete'
                : '';

          return (
            <li
              aria-current={stepNumber === activeStep ? 'step' : undefined}
              className={state}
              key={step}
            >
              <span>{stepNumber}</span>
              {step}
            </li>
          );
        })}
      </ol>
    </aside>
  );
};

const GuidedBuilder = ({ builderKind, onStepChange }) => {
  const copy = BUILDER_COPY[builderKind] || BUILDER_COPY.experiment;
  const [fields, setFields] = useState({
    challenge: '',
    affected: '',
    constraint: '',
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const resultRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (result) resultRef.current?.focus();
  }, [result]);

  const updateField = (name, value) => {
    setFields((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: '' }));

    if (name !== 'challenge' || value.trim()) onStepChange(2);
  };

  const validate = () => {
    const nextErrors = {};

    if (!fields.challenge.trim()) {
      nextErrors.challenge = 'Describe the change you want to explore.';
    }
    if (!fields.affected.trim()) {
      nextErrors.affected = 'Name the people or organisations affected.';
    }
    if (!fields.constraint.trim()) {
      nextErrors.constraint = 'Add the most important constraint or context.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submit = (event) => {
    event.preventDefault();
    if (!validate()) return;

    setBusy(true);
    onStepChange(2);

    timerRef.current = window.setTimeout(() => {
      setResult(buildPrototypePlan(fields, builderKind));
      setBusy(false);
      onStepChange(3);
    }, 650);
  };

  const clearForm = () => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    setFields({ challenge: '', affected: '', constraint: '' });
    setErrors({});
    setBusy(false);
    setResult(null);
    onStepChange(1);
  };

  return (
    <div className="stw-builder">
      <div className="stw-workspace-heading">
        <h1>{copy.heading}</h1>
        <p>{copy.intro}</p>
      </div>

      <form noValidate onSubmit={submit}>
        <div className="stw-field stw-field--challenge">
          <label htmlFor="tool-challenge">{copy.challengeLabel}</label>
          <textarea
            aria-describedby={
              errors.challenge ? 'tool-challenge-error' : undefined
            }
            aria-invalid={Boolean(errors.challenge)}
            id="tool-challenge"
            onChange={(event) => updateField('challenge', event.target.value)}
            placeholder={copy.challengePlaceholder}
            rows="4"
            value={fields.challenge}
          />
          {errors.challenge ? (
            <span className="stw-field__error" id="tool-challenge-error">
              {errors.challenge}
            </span>
          ) : null}
        </div>

        <div className="stw-field-grid">
          <div className="stw-field">
            <label htmlFor="tool-affected">{copy.affectedLabel}</label>
            <input
              aria-describedby={
                errors.affected ? 'tool-affected-error' : undefined
              }
              aria-invalid={Boolean(errors.affected)}
              id="tool-affected"
              onChange={(event) => updateField('affected', event.target.value)}
              placeholder={copy.affectedPlaceholder}
              type="text"
              value={fields.affected}
            />
            {errors.affected ? (
              <span className="stw-field__error" id="tool-affected-error">
                {errors.affected}
              </span>
            ) : null}
          </div>

          <div className="stw-field">
            <label htmlFor="tool-constraint">{copy.constraintLabel}</label>
            <input
              aria-describedby={
                errors.constraint ? 'tool-constraint-error' : undefined
              }
              aria-invalid={Boolean(errors.constraint)}
              id="tool-constraint"
              onChange={(event) => updateField('constraint', event.target.value)}
              placeholder={copy.constraintPlaceholder}
              type="text"
              value={fields.constraint}
            />
            {errors.constraint ? (
              <span className="stw-field__error" id="tool-constraint-error">
                {errors.constraint}
              </span>
            ) : null}
          </div>
        </div>

        <div className="stw-form-actions">
          <button
            aria-busy={busy}
            className="stw-primary-action"
            disabled={busy}
            type="submit"
          >
            <ToolGlyph name="sparkles" size={21} />
            {busy ? 'Building plan…' : copy.submitLabel}
          </button>
          <button
            className="stw-secondary-action"
            disabled={busy}
            onClick={clearForm}
            type="button"
          >
            Clear
          </button>
          <p>
            <ToolGlyph name="info" size={18} />
            Review and refine the plan before using it.
          </p>
        </div>
      </form>

      {result ? (
        <section
          aria-labelledby="prototype-plan-title"
          className="stw-result"
          ref={resultRef}
          tabIndex="-1"
        >
          <div className="stw-result__heading">
            <h2 id="prototype-plan-title">{copy.resultHeading}</h2>
            <button onClick={clearForm} type="button">
              <ToolGlyph name="reset" size={18} />
              Start again
            </button>
          </div>

          <div className="stw-result__items">
            {copy.resultItems.map((item) => (
              <article key={item.key}>
                <span aria-hidden="true">
                  <ToolGlyph name={item.icon} size={23} />
                </span>
                <div>
                  <h3>{item.label}</h3>
                  <p>{result[item.key]}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
};

const ChatPrototype = ({ onStepChange, tool }) => {
  const [messages, setMessages] = useState([
    { id: 'welcome', role: 'assistant', text: tool.welcomeMessage },
  ]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    },
    [],
  );

  const submit = (event) => {
    event.preventDefault();
    const cleanMessage = message.trim();

    if (!cleanMessage) {
      setError('Enter a question or choose a starting point.');
      inputRef.current?.focus();
      return;
    }

    setMessages((current) => [
      ...current,
      { id: `user-${Date.now()}`, role: 'user', text: cleanMessage },
    ]);
    setMessage('');
    setError('');
    setBusy(true);
    onStepChange(2);

    timerRef.current = window.setTimeout(() => {
      setMessages((current) => [
        ...current,
        {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          text: tool.prototypeResponse,
        },
      ]);
      setBusy(false);
      onStepChange(3);
    }, 650);
  };

  return (
    <div className="stw-chat">
      <div className="stw-workspace-heading">
        <h1>Ask a question.</h1>
        <p>
          Start with a question or choose a prompt. Review the response before
          using it in your work.
        </p>
      </div>

      <div
        aria-label={`${tool.title} conversation`}
        aria-live="polite"
        className="stw-conversation"
        role="log"
      >
        {messages.map((item) => (
          <article className={`stw-message stw-message--${item.role}`} key={item.id}>
            <span>{item.role === 'assistant' ? 'Supernova Tools' : 'You'}</span>
            <p>{item.text}</p>
          </article>
        ))}
        {busy ? (
          <div className="stw-message stw-message--thinking" role="status">
            <span>Supernova Tools</span>
            <p>Preparing a response…</p>
          </div>
        ) : null}
      </div>

      {messages.length === 1 ? (
        <div className="stw-prompt-suggestions" aria-label="Starting prompts">
          {tool.promptSuggestions.map((prompt) => (
            <button
              key={prompt}
              onClick={() => {
                setMessage(prompt);
                setError('');
                inputRef.current?.focus();
              }}
              type="button"
            >
              {prompt}
            </button>
          ))}
        </div>
      ) : null}

      <form className="stw-chat-form" onSubmit={submit}>
        <label className="visually-hidden" htmlFor="tool-chat-message">
          Your question
        </label>
        <textarea
          aria-describedby={error ? 'tool-chat-error' : 'tool-chat-notice'}
          aria-invalid={Boolean(error)}
          id="tool-chat-message"
          onChange={(event) => {
            setMessage(event.target.value);
            setError('');
          }}
          placeholder="Write a question or describe what you are working through…"
          ref={inputRef}
          rows="3"
          value={message}
        />
        <button disabled={busy} type="submit">
          <span>{busy ? 'Working…' : 'Send'}</span>
          <ToolGlyph name="send" size={20} />
        </button>
      </form>
      {error ? (
        <p className="stw-field__error" id="tool-chat-error">
          {error}
        </p>
      ) : (
        <p className="stw-chat-notice" id="tool-chat-notice">
          <ToolGlyph name="info" size={17} />
          Messages are cleared when you leave or reset this tool.
        </p>
      )}
    </div>
  );
};

const ToolUnavailable = ({ tool }) => (
  <div className="stw-unavailable">
    <span aria-hidden="true">
      <ToolGlyph name={tool.icon} size={40} />
    </span>
    <h1>{tool.title} is coming soon</h1>
    <p>This interactive experience is not available yet.</p>
    <Link to="/supernova-tools">
      <ToolGlyph name="back" size={19} />
      Return to all tools
    </Link>
  </div>
);

const ToolWorkspacePage = ({ embedded = false }) => {
  const { slug } = useParams();
  const [tool, setTool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(1);
  const [resetVersion, setResetVersion] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    const loadTool = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await getSupernovaTool(slug, {
          signal: controller.signal,
        });
        setTool(response.data);
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          setError('This tool could not be loaded.');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadTool();
    return () => controller.abort();
  }, [slug]);

  const resetWorkspace = () => {
    setActiveStep(1);
    setResetVersion((version) => version + 1);
  };

  return (
    <div
      className={
        embedded
          ? 'tool-workspace-page tool-workspace-page--embedded'
          : 'tool-workspace-page'
      }
    >
      <SEO
        title={
          tool
            ? `${tool.title} | Supernova Tools`
            : 'Supernova Tools Workspace'
        }
        description={tool?.longDescription}
      />

      <ToolWorkspaceHeader
        onReset={resetWorkspace}
        resetDisabled={loading || !tool || tool.experience === 'unavailable'}
        title={tool?.title}
      />

      {loading ? (
        <main className="stw-loading" id="tool-workspace-main">
          <span />
          <p>Preparing the tool workspace…</p>
        </main>
      ) : error ? (
        <main className="stw-page-state" id="tool-workspace-main" role="alert">
          <ToolGlyph name="info" size={34} />
          <h1>We could not open this tool</h1>
          <p>{error}</p>
          <Link to="/supernova-tools">Return to all tools</Link>
        </main>
      ) : !tool ? (
        <main className="stw-page-state" id="tool-workspace-main">
          <ToolGlyph name="search" size={34} />
          <h1>Tool not found</h1>
          <p>The requested tool is not part of the current catalogue.</p>
          <Link to="/supernova-tools">Browse Supernova Tools</Link>
        </main>
      ) : tool.experience === 'unavailable' ? (
        <main id="tool-workspace-main">
          <ToolUnavailable tool={tool} />
        </main>
      ) : (
        <main className="stw-layout" id="tool-workspace-main">
          <ToolContextRail activeStep={activeStep} tool={tool} />
          <section className="stw-canvas">
            {tool.experience === 'builder' ? (
              <GuidedBuilder
                builderKind={tool.builderKind}
                key={`${tool.id}-${resetVersion}`}
                onStepChange={setActiveStep}
              />
            ) : (
              <ChatPrototype
                key={`${tool.id}-${resetVersion}`}
                onStepChange={setActiveStep}
                tool={tool}
              />
            )}
          </section>
        </main>
      )}
    </div>
  );
};

export default ToolWorkspacePage;
